import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, HelpCircle, Play } from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  AppButton, AppCard, AppCardHeader, AppCardTitle, AppDataTable, Callout, ConfirmModal,
  EmptyState, StatusBadge,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import AssessmentBriefing from "./AssessmentBriefing";
import { formatSchedule, sectionsOf } from "./studentAssessmentRules";

function AttemptsTable({ attempts, navigate }) {
  if (!attempts.length) return null;
  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle as="h2">Previous attempts</AppCardTitle>
      </AppCardHeader>
      <AppDataTable
        fill={false}
        rows={attempts}
        rowKey={(a) => a._id}
        columns={[
          { key: "attemptNumber", label: "#", align: "right", render: (a) => <span className="tw-tabular-nums">{a.attemptNumber}</span> },
          { key: "score", label: "Score", render: (a) => <span className="tw-tabular-nums">{a.score}/{a.totalMarks}</span> },
          { key: "percentage", label: "Percentage", align: "right", render: (a) => <span className="tw-tabular-nums">{a.percentage}%</span> },
          {
            key: "passed",
            label: "Result",
            render: (a) => (
              <StatusBadge status={a.passed ? "passed" : "failed"} domain="assessmentResult" />
            ),
          },
          { key: "submittedAt", label: "Date", render: (a) => (a.submittedAt ? new Date(a.submittedAt).toLocaleDateString() : "—") },
          {
            key: "action",
            label: "",
            align: "right",
            render: (a) => (
              <AppButton size="xs" variant="outline" onClick={() => navigate(`/s/assessment/result/${a._id}`)}>
                Review
              </AppButton>
            ),
          },
        ]}
      />
    </AppCard>
  );
}

/**
 * AssessmentInfoPage — `/s/assessment/:id`, what the student reads before
 * starting (AssessmentBriefing — the card a staff preview shows too), the start
 * button, and their earlier attempts.
 */
const AssessmentInfoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [assessment, setAssessment] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [state, setState] = useState("loading");
  const [starting, setStarting] = useState(false);
  const [confirmStart, setConfirmStart] = useState(false);
  const [failure, setFailure] = useState("");

  const fetchData = useCallback(async () => {
    setState("loading");
    try {
      const [infoRes, attemptsRes] = await Promise.all([
        apiClient.get(`/student-assessments/${id}/info`),
        apiClient.get(`/student-assessments/${id}/my-attempts`),
      ]);
      setAssessment(infoRes.data.assessment);
      setAttempts(attemptsRes.data.attempts || []);
      setState("ready");
    } catch (err) {
      setState(err?.response?.status === 404 || err?.response?.status === 403 ? "missing" : "error");
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStart = async () => {
    setStarting(true);
    setFailure("");
    try {
      const { data } = await apiClient.post(`/student-assessments/${id}/start`);
      navigate(`/s/assessment/take/${data.attempt._id}`);
    } catch (err) {
      setFailure(err.response?.data?.message || "The assessment could not be started. Try again.");
    } finally {
      setStarting(false);
      setConfirmStart(false);
    }
  };

  const back = (
    <div>
      <AppButton variant="ghost" size="sm" onClick={() => navigate("/s/assessments")}>
        <ArrowLeft size={16} aria-hidden="true" /> Back to Assessments
      </AppButton>
    </div>
  );

  if (state === "loading") {
    return (
      <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-3xl tw-flex-col tw-gap-4" aria-busy="true" aria-label="Loading assessment">
        <Shimmer height={32} />
        <Shimmer height={360} />
      </div>
    );
  }

  if (state !== "ready" || !assessment) {
    return (
      <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-3xl tw-flex-col tw-gap-4">
        {back}
        {state === "error" ? (
          <Callout tone="danger" role="alert" action={<AppButton size="xs" variant="outline" onClick={fetchData}>Try again</AppButton>}>
            This assessment could not be loaded.
          </Callout>
        ) : (
          <EmptyState icon={HelpCircle} title="Assessment not found" description="It may have been removed, or it is not assigned to you." />
        )}
      </div>
    );
  }

  const a = assessment;
  const maxReached = a.maxAttempts > 0 && a.attemptsUsed >= a.maxAttempts;
  const now = new Date();
  const isExpired = a.endDate && new Date(a.endDate) < now;
  const notStartedYet = a.startDate && new Date(a.startDate) > now;
  const canStart = !maxReached && !isExpired && !notStartedYet;
  const sequential = a.sectionNavigation === "sequential" && sectionsOf(a).length > 1;

  const disabledLabel = () => {
    if (isExpired) return "Expired";
    if (notStartedYet) return `Opens ${formatSchedule(a.startDate)}`;
    return "No attempts left";
  };

  const startButton = () => {
    if (a.inProgressAttemptId) {
      return (
        <AppButton onClick={() => navigate(`/s/assessment/take/${a.inProgressAttemptId}`)}>
          <Play size={16} aria-hidden="true" /> Continue assessment
        </AppButton>
      );
    }
    if (canStart) {
      return (
        <AppButton onClick={() => setConfirmStart(true)} disabled={starting}>
          <Play size={16} aria-hidden="true" /> {starting ? "Starting…" : "Start assessment"}
        </AppButton>
      );
    }
    return <AppButton variant="outline" disabled>{disabledLabel()}</AppButton>;
  };

  const confirmMessage = a.timeLimit > 0
    ? `You are about to start "${a.title}". You will have ${a.timeLimit} minutes${sequential ? ", each section on its own timer" : ""}. Are you ready?`
    : `You are about to start "${a.title}". Are you ready?`;

  return (
    <div className="tw-mx-auto tw-flex tw-w-full tw-max-w-3xl tw-flex-col tw-gap-4">
      {back}

      {failure && <Callout tone="danger" role="alert">{failure}</Callout>}

      <AssessmentBriefing assessment={a} action={startButton()} />

      <AttemptsTable attempts={attempts} navigate={navigate} />

      <ConfirmModal
        show={confirmStart}
        onHide={() => setConfirmStart(false)}
        onConfirm={handleStart}
        title="Start Assessment"
        message={confirmMessage}
        confirmText="Start Now"
        variant="info"
        loading={starting}
      />
    </div>
  );
};

export default AssessmentInfoPage;
